import { useSessionStore } from "@auth/sessionStore";
import { useTaskActions } from "@notifications/useTaskActions";
import { byUrgencyFirst } from "@subjects/cardSeverity";
import { useSuspenseQueries } from "@tanstack/react-query";
import { HUMAN, outstandingTasks, SIGN, type Task } from "@tasks/tasksClient";
import { subjectOpenTasksQueryOptions } from "@tasks/tasksQueries";
import type { View } from "@views/viewsClient";
import {
  surfacedViewsQueryOptions,
  viewsListQueryOptions,
} from "@views/viewsQueries";
import { useMemo } from "react";

/** Fallback order for uncurated views: most recently updated first. */
const latestFirst = (views: View[]): View[] =>
  [...views].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

export interface SubjectIndex {
  /**
   * Open tasks, most urgent first (ties keep their soonest-due order).
   * Signature requests aren't here — they read off their view.
   */
  tasks: Task[];
  /** The agent's surfaced set, in its order (BE-1909); empty until curated. */
  surfaced: View[];
  /** The rest, most recently updated first. */
  other: View[];
  /** `surfaced` then `other` — the reader's selection domain. */
  views: View[];
  handleAction: (
    task: Task,
    action: string,
    data: Record<string, unknown>,
  ) => void;
}

/**
 * Everything the subject rail indexes (WEB-3288): open tasks, and views split into
 * the agent's surfaced set and the rest. Surfacing ranks, it doesn't filter.
 *
 * One `useSuspenseQueries` rather than three hooks: separate suspense queries
 * resolve serially, since the first to throw stops the rest from ever running.
 * Callers must sit under an `AsyncBoundary`.
 */
export const useSubjectIndex = (subjectId: string): SubjectIndex => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const [{ data: all }, { data: surfaced }, { data: subjectTasks }] =
    useSuspenseQueries({
      queries: [
        viewsListQueryOptions(sessionKey, subjectId),
        surfacedViewsQueryOptions(sessionKey, subjectId),
        subjectOpenTasksQueryOptions(sessionKey, subjectId),
      ],
    });
  const { handleAction, isAnswered } = useTaskActions();

  // Stable sort over an already due-date-ordered list: severity, then due date.
  // `sign` is left out: a signature request belongs to its view, and the view's
  // own task bell is where it's actioned.
  // `human` to-dos hide the instant they're ticked; a2ui cards stay until the
  // server confirms, so a failed submit stays visible to retry (WEB-3320).
  const tasks = useMemo(
    () =>
      outstandingTasks(subjectTasks)
        .filter(
          (task) =>
            task.kind !== SIGN && !(task.kind === HUMAN && isAnswered(task.id)),
        )
        .sort(byUrgencyFirst),
    [subjectTasks, isAnswered],
  );

  const { other, views } = useMemo(() => {
    const surfacedIds = new Set(surfaced.map((view) => view.id));
    const rest = latestFirst(all.filter((view) => !surfacedIds.has(view.id)));
    return { other: rest, views: [...surfaced, ...rest] };
  }, [all, surfaced]);

  return { tasks, surfaced, other, views, handleAction };
};
